import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  Loader2,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';

const AdminTeamsPage = () => {
  const { api } = useAuth();
  const { isTrialExpired } = useTrialStatus();
  
  // Mark this page as visited
  useMarkPageVisited('/admin/teams');
  
  const [teams, setTeams] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    supplier_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, suppliersRes, usersRes] = await Promise.all([
        api.get('/teams'),
        api.get('/suppliers/all'),
        api.get('/users')
      ]);
      setTeams(teamsRes.data);
      setSuppliers(suppliersRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(search.toLowerCase())
  );

  const getUserCountByTeam = (teamName, role = null) => {
    const teamUsers = users.filter(user => user.team_name === teamName);
    
    if (role === 'responsable') {
      return teamUsers.filter(user => user.is_responsable === true).length;
    } else if (role === 'user') {
      // Utilisateur standard = ni admin ni responsable
      return teamUsers.filter(user => !user.is_admin && !user.is_responsable).length;
    }
    
    return teamUsers.length;
  };

  const handleOpenDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        supplier_ids: team.supplier_ids || []
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        supplier_ids: []
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Le nom de l\'équipe est obligatoire');
      return;
    }

    setFormLoading(true);
    try {
      if (editingTeam) {
        await api.put(`/teams/${editingTeam.id}`, formData);
        toast.success('Équipe modifiée');
      } else {
        await api.post('/teams', formData);
        toast.success('Équipe créée');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'opération'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette équipe ?')) return;
    
    try {
      await api.delete(`/teams/${teamId}`);
      toast.success('Équipe supprimée');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const toggleSupplier = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      supplier_ids: prev.supplier_ids.includes(supplierId)
        ? prev.supplier_ids.filter(id => id !== supplierId)
        : [...prev.supplier_ids, supplierId]
    }));
  };

  const getSupplierNames = (supplierIds) => {
    return supplierIds
      .map(id => suppliers.find(s => s.id === id)?.name)
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in relative" data-testid="admin-teams-page">
      {isTrialExpired && <TrialExpiredOverlay />}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Équipes</h1>
          <p className="text-gray-500 mt-1">Gestion des équipes et de leurs fournisseurs</p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Organisez vos équipes (Cuisine, Salle, Bar...) et attribuez-leur les fournisseurs accessibles lors de leurs commandes.
          </p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
          data-testid="add-team-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une équipe
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Rechercher une équipe..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="teams-search-input"
        />
      </div>

      <div className="grid gap-4">
        {filteredTeams.map((team) => (
          <Card key={team.id} className="border border-gray-200" data-testid={`team-card-${team.name}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D8F3DC] flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#1B4332]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{team.name}</h3>
                    
                    {/* Compteurs utilisateurs */}
                    <div className="flex items-center gap-3 mt-2 mb-2">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span className="text-xs text-gray-600">
                          <span className="font-semibold">{getUserCountByTeam(team.name, 'responsable')}</span> Responsable{getUserCountByTeam(team.name, 'responsable') > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-xs text-gray-600">
                          <span className="font-semibold">{getUserCountByTeam(team.name, 'user')}</span> Utilisateur{getUserCountByTeam(team.name, 'user') > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      <Truck className="w-4 h-4 text-gray-400" />
                      {getSupplierNames(team.supplier_ids).map((name) => (
                        <Badge key={name} variant="secondary" className="text-xs">
                          {name}
                        </Badge>
                      ))}
                      {team.supplier_ids.length === 0 && (
                        <span className="text-sm text-gray-400">Aucun fournisseur assigné</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => handleOpenDialog(team)}
                    data-testid={`edit-team-${team.name}-btn`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="outline"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(team.id)}
                    data-testid={`delete-team-${team.name}-btn`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTeams.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Aucune équipe trouvée</h3>
          <p className="text-gray-500">Créez votre première équipe</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingTeam ? 'Modifier l\'équipe' : 'Nouvelle équipe'}
            </DialogTitle>
            <DialogDescription>
              {editingTeam ? 'Modifiez les informations de l\'équipe' : 'Créez une nouvelle équipe'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de l'équipe *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Cuisine, Salle, Bar..."
                data-testid="team-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Fournisseurs accessibles</Label>
              <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-3">
                {suppliers.map((supplier) => (
                  <div key={supplier.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`supplier-${supplier.id}`}
                      checked={formData.supplier_ids.includes(supplier.id)}
                      onCheckedChange={() => toggleSupplier(supplier.id)}
                      data-testid={`team-supplier-${supplier.name}-checkbox`}
                    />
                    <Label 
                      htmlFor={`supplier-${supplier.id}`}
                      className="font-normal cursor-pointer flex-1"
                    >
                      {supplier.name}
                      <span className="text-sm text-gray-500 ml-2">({supplier.type})</span>
                    </Label>
                  </div>
                ))}
                {suppliers.length === 0 && (
                  <p className="text-sm text-gray-500">Aucun fournisseur disponible</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={formLoading}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
              data-testid="save-team-btn"
            >
              {formLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeamsPage;
